"use client";
import { ToggleGroup } from "@cmp-src/primitives/toggle-group";
import React from "react";
import {
  isReleaseDeskSelectionComplete,
  type ReleaseDeskChoice,
  type ReleaseDeskSelection
} from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { ReleaseGranteesPicker } from "./ReleaseGranteesPicker";

export interface ReleaseDeskOptionProps {
  deskName: string;
  selection: ReleaseDeskSelection;
  onSelectionChange: (selection: ReleaseDeskSelection) => void;
  /** The days the release covers, as the hint reads them; "these days" by default. */
  dayLabel?: string;
}

const CHOICES: { value: ReleaseDeskChoice; label: string }[] = [
  { value: "keep", label: "Keep it" },
  { value: "open", label: "Anyone" },
  { value: "assigned", label: "Specific people" }
];

const isReleaseDeskChoice = (value: string): value is ReleaseDeskChoice =>
  CHOICES.some((choice) => choice.value === value);

const hintFor = (choice: ReleaseDeskChoice, dayLabel: string): string => {
  switch (choice) {
    case "keep":
      return "Your desk stays yours.";
    case "open":
      return `Anyone can book it ${dayLabel}. Your own reservations on it are cancelled.`;
    case "assigned":
      return `Only the teams and people you pick can book it ${dayLabel}. Your own reservations on it are cancelled.`;
  }
};

/**
 * The extra for people with a dedicated desk, in the cancel-reservations
 * tool and the working location prompt: keep the desk, or free it for the
 * same days, to anyone or to specific teams and people, so it is not sitting
 * empty while they are away.
 */
export const ReleaseDeskOption: React.FC<ReleaseDeskOptionProps> = ({
  deskName,
  selection,
  onSelectionChange,
  dayLabel = "these days"
}) => {
  const update = (patch: Partial<ReleaseDeskSelection>) =>
    onSelectionChange({ ...selection, ...patch });
  const needsGrantees = !isReleaseDeskSelectionComplete(selection);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="font-medium text-neutral-800 text-sm">
            Your desk, {deskName}
          </p>
          <p className="text-neutral-500 text-xs">
            {hintFor(selection.choice, dayLabel)}
          </p>
        </div>
        <ToggleGroup
          items={CHOICES}
          value={selection.choice}
          onValueChange={(value) => {
            if (isReleaseDeskChoice(value)) {
              update({ choice: value });
            }
          }}
        />
      </div>
      {selection.choice === "assigned" && (
        <div className="flex flex-col gap-2">
          <ReleaseGranteesPicker
            users={selection.users}
            teams={selection.teams}
            onUsersChange={(users) => update({ users })}
            onTeamsChange={(teams) => update({ teams })}
          />
          {needsGrantees && (
            <p className="text-destructive text-xs">
              Pick at least one team or person.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
